import { PageOf, PageQuery } from '@/models'

import axios, { withToken } from './axios'

import { assertFile } from '@/utils'

type parser<R, E> = (resource: R) => E

/**
 * Generates a Create async function that creates a resource R
 * with a payload P and returns an element E.
 *
 * @param url Base URL to resource R.
 * @param parser Function that converts R to E.
 */
export function generateCreate<E, R, P> (urlPrefix: string, entity: string, parser: parser<R, E>) {
  return async function createResource (tokenV2: string, accountId: number, shopId: number, payload: P): Promise<E> {
    const response = await axios.post<R>(
      `${urlPrefix}/accounts/${accountId}/shops/${shopId}/${entity}`,
      payload,
      withToken(tokenV2)
    )

    return parser(response.data)
  }
}

/**
 * Generates a Delete async function that deletes an existing resource.
 *
 * @param url Base URL to resource.
 */
export function generateDelete (urlPrefix: string, entity: string) {
  return async function deleteResource (tokenV2: string, accountId: number, shopId: number, id: number): Promise<void> {
    await axios.delete<void>(
      `${urlPrefix}/accounts/${accountId}/shops/${shopId}/${entity}/${id}`,
      withToken(tokenV2)
    )
  }
}

/**
 * Generates a Read async function that fetches a resource R and
 * returns an element E.
 *
 * @param url Base URL to resource R.
 * @param parser Function that converts R to E.
 */
export function generateRead<E, R> (urlPrefix: string, entity: string, parser: parser<R, E>) {
  return async function readResource (tokenV2: string, accountId: number, shopId: number, id: number): Promise<E> {
    const response = await axios.get<R>(
      `${urlPrefix}/accounts/${accountId}/shops/${shopId}/${entity}/${id}`,
      withToken(tokenV2)
    )

    return parser(response.data)
  }
}

/**
 * Generates a Read async function that fetches a page P of resources
 * with query options Q and returns a PageOf<E>.
 *
 * @param url Base URL to page P of resources.
 * @param pageParser Function that converts a page P to a PageOf<E>.
 */
export function generateReadPage<E, P, Q extends PageQuery> (urlPrefix: string, entity: string, pageParser: parser<P, PageOf<E>>) {
  return async function readPage (tokenV2: string, accountId: number, shopId: number, query: Q): Promise<PageOf<E>> {
    const response = await axios.get<P>(
      `${urlPrefix}/accounts/${accountId}/shops/${shopId}/${entity}`,
      {
        params: query,
        ...withToken(tokenV2)
      }
    )

    return pageParser(response.data)
  }
}

/**
 * Generates an Update async function that updates an existing
 * resource R with a payload P and returns an element E.
 *
 * @param url Base URL to resource R.
 * @param parser Function that converts R to E.
 */
export function generateUpdate<E, R, P> (urlPrefix: string, entity: string, parser: parser<R, E>) {
  return async function updateResource (tokenV2: string, accountId: number, shopId: number, id: number, payload: P): Promise<E> {
    const response = await axios.put<R>(
      `${urlPrefix}/accounts/${accountId}/shops/${shopId}/${entity}/${id}`,
      payload,
      withToken(tokenV2)
    )

    return parser(response.data)
  }
}

/**
 * Generates an Update async function that updates a subresource of
 * an existing resource by performing an empty PUT request.
 *
 * @param url Base URL to resource.
 * @param subresource Subresource to receive the PUT request.
 */
export function generateUpdateSubresource (urlPrefix: string, entity: string, subresource: string) {
  return async function updateSubresource (tokenV2: string, accountId: number, shopId: number, id: number): Promise<void> {
    await axios.put<void>(
      `${urlPrefix}/accounts/${accountId}/shops/${shopId}/${entity}/${id}/${subresource}`,
      null,
      withToken(tokenV2)
    )
  }
}

/**
 * Generates an UpdateImage async function that updates the image
 * of a resource R and returns an element E.
 *
 * @param url Base URL to resource R.
 * @param parser Function that converts R to E.
 */
export function generateUpdateImage<R, E> (urlPrefix: string, entity: string, parser: parser<R, E>) {
  return async function updateImage (
    tokenV2: string,
    accountId: number,
    shopId: number,
    id: number,
    formData: FormData
  ) {
    assertFile(formData)

    const config = withToken(tokenV2)
    config.headers['Content-Type'] = 'multipart/form-data'

    const response = await axios.put<R>(
      `${urlPrefix}/accounts/${accountId}/shops/${shopId}/${entity}/${id}/image`,
      formData,
      config
    )

    return parser(response.data)
  }
}
